import { useEffect, useRef, useState } from 'react';

interface CountUpProps {
  /** The final value. Pass `null`/`undefined` while loading. */
  value: number | null | undefined;
  /** Render the eventual number — keeps formatting concerns at the call site. */
  format?: (n: number) => string;
  /** Total animation duration in ms. */
  durationMs?: number;
  /** Placeholder while value is undefined. */
  placeholder?: string;
}

/**
 * Animates a number from 0 to `value` once, on the transition from
 * undefined/null to defined. Subsequent value changes (e.g. polling refetch)
 * snap to the new value without re-animating — that would be visual noise.
 *
 * Honours prefers-reduced-motion: snaps directly to the final value.
 */
export function CountUp({
  value,
  format = (n) => Math.round(n).toLocaleString('en-US'),
  durationMs = 400,
  placeholder = '—',
}: CountUpProps) {
  const [display, setDisplay] = useState<number | null>(
    typeof value === 'number' && Number.isFinite(value) ? value : null,
  );
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return;

    // Already counted up once — snap to subsequent values without re-running.
    if (hasAnimatedRef.current) {
      setDisplay(value);
      return;
    }
    hasAnimatedRef.current = true;

    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reduce || durationMs <= 0) {
      setDisplay(value);
      return;
    }

    const start = performance.now();
    const from = 0;
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // easeOutQuad — calm, no overshoot.
      const eased = 1 - (1 - t) * (1 - t);
      setDisplay(from + (value - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setDisplay(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, durationMs]);

  if (display === null) return <>{placeholder}</>;
  return <>{format(display)}</>;
}
